import { useEffect, useMemo, useState } from 'react'
import { CircleAlert, CircleCheck, CircleX } from 'lucide-react'
import DataTable from '../../components/DataTable'
import { useRemembered } from '../../components/listMemory'
import PageHeader from '../../components/PageHeader'
import StatStrip from '../../components/StatStrip'
import StatusBadge from '../../components/StatusBadge'
import TabBar from '../../components/TabBar'
import useHashTab from '../../components/useHashTab'
import { listSettingsKeys } from './settingsService'
import { settingsKeysStrings as S } from './strings'
import './settings.css'

/**
 * A key resolves to whichever value the system will actually use:
 * the database wins, the coded default is the fallback, and neither means the key is
 * unset. The three counters at the top of the legacy page count exactly these.
 */
/** The three key states, mapped onto the strip's tones. */
const TONES = { good: 'success', warn: 'warning', bad: 'danger' }

const STATES = [
  { id: 'db', tone: 'good', icon: CircleCheck, label: S.databaseValue },
  { id: 'default', tone: 'warn', icon: CircleAlert, label: S.defaultValue },
  { id: 'none', tone: 'bad', icon: CircleX, label: S.none },
]

const stateOf = (row) => {
  if (row.dbValue !== null) return 'db'
  if (row.defaultValue !== null) return 'default'
  return 'none'
}

/* The two views this page answers to in its URL hash — the same contract every other
   tabbed screen here uses, so `#reference` opens the reference tables rather than the
   page's first tab. See components/useHashTab.js. */
const VIEWS = ['keys', 'reference']

/** بيانات الإعدادات — Views/SettingsKeys/Index.cshtml. */
export default function SettingsKeysPage() {
  const [rows, setRows] = useState([])
  const [reference, setReference] = useState([])
  const [ffmpegExists, setFfmpegExists] = useState(true)
  const [loading, setLoading] = useState(true)
  /* Which of the three counters is narrowing the list — remembered, because reading a
     key's value means opening something else and coming back, and the filter that found
     it went with the page. */
  const [filter, setFilter] = useRemembered('settings-keys:filter', null)
  const [view, setView] = useHashTab('keys', VIEWS)

  useEffect(() => {
    listSettingsKeys().then((response) => {
      setRows(response.rows)
      setReference(response.reference)
      setFfmpegExists(response.ffmpegExists)
      setLoading(false)
    })
  }, [])

  /* The reference tables matter when something is MISSING, so the count that leads is
     the count of gaps — not the total, which is a constant of the codebase. */
  const missingReference = useMemo(
    () => reference.filter((row) => !row.existsInDb).length,
    [reference],
  )

  const referenceColumns = useMemo(
    () => [
      {
        key: 'existsInDb',
        header: S.reference.status,
        width: '110px',
        searchable: false,
        value: (row) => (row.existsInDb ? 1 : 0),
        render: (row) => (
          <StatusBadge tone={row.existsInDb ? 'success' : 'danger'}>
            {row.existsInDb ? S.reference.present : S.reference.missing}
          </StatusBadge>
        ),
      },
      {
        key: 'group',
        header: S.reference.group,
        render: (row) => <bdi>{S.reference.groups[row.group] ?? row.group}</bdi>,
      },
      { key: 'id', header: S.reference.id, width: '72px' },
      {
        key: 'enumName',
        header: S.reference.enumName,
        render: (row) => (
          <span className="set-mono" dir="ltr">
            {row.enumName}
          </span>
        ),
      },
      {
        key: 'dbName',
        header: S.reference.dbName,
        render: (row) => <bdi>{row.dbName}</bdi>,
      },
    ],
    [],
  )

  const counts = useMemo(
    () =>
      rows.reduce(
        (totals, row) => ({ ...totals, [stateOf(row)]: (totals[stateOf(row)] ?? 0) + 1 }),
        {},
      ),
    [rows],
  )

  const visible = useMemo(
    () => (filter ? rows.filter((row) => stateOf(row) === filter) : rows),
    [rows, filter],
  )

  const columns = useMemo(
    () => [
      {
        key: 'state',
        header: '',
        width: '56px',
        align: 'center',
        searchable: false,
        /* The legacy sorts this column on the same 0/1/2 order the icon encodes. */
        value: (row) => STATES.findIndex((state) => state.id === stateOf(row)),
        render: (row) => {
          const state = STATES.find((item) => item.id === stateOf(row))
          const Icon = state.icon
          return (
            <span className={`sk-mark sk-mark--${state.tone}`} title={state.label}>
              <Icon size={18} strokeWidth={2} />
            </span>
          )
        },
      },
      {
        key: 'key',
        header: S.key,
        width: '340px',
        render: (row) => (
          <span className="set-mono" dir="ltr">
            {row.key}
          </span>
        ),
      },
      {
        key: 'defaultValue',
        header: S.defaultValue,
        render: (row) => (
          <span className="set-mono set-mono--muted" dir="ltr">
            {row.defaultValue}
          </span>
        ),
      },
      {
        key: 'dbValue',
        header: S.databaseValue,
        render: (row) => (
          <span className="set-mono" dir="ltr">
            {row.dbValue}
          </span>
        ),
      },
    ],
    [],
  )

  return (
    <>
      {/* The title was printed three times: header, header subtitle, table card. */}
      <PageHeader title={S.title} count={loading ? undefined : rows.length} />

      {/* Two things share this screen: what the installation is SET to, and whether the
          database still has the reference rows the code expects. Tabs rather than one
          long page, because they are read for different reasons. */}
      <TabBar
        items={[
          { id: 'keys', label: S.title },
          {
            id: 'reference',
            label: missingReference
              ? `${S.reference.title} (${missingReference})`
              : S.reference.title,
          },
        ]}
        value={view}
        onChange={setView}
        ariaLabel={S.title}
      />

      {/* The three counters become the filter. They were already computed and
          already sitting above the table; clicking one is the obvious thing to
          try, and until now it did nothing. */}
      {view === 'keys' && (
        <>
      <StatStrip
        items={STATES.map((state) => ({
          id: state.id,
          icon: state.icon,
          label: state.label,
          value: counts[state.id] ?? 0,
          tone: TONES[state.tone],
        }))}
        label={S.title}
        activeId={filter}
        onSelect={(id) => setFilter((current) => (current === id ? null : id))}
      />

      {/*
       * Paged.
       *
       * The legacy turns paging off and prints all six hundred keys, which is a
       * nine-thousand-pixel page you can only navigate with the scrollbar — by
       * a distance the least readable screen in the panel. Nothing is hidden:
       * the search box spans every key and the three counters above narrow by
       * state, which is how anyone actually looks for one.
       */}
      <DataTable
        columns={columns}
        rows={visible}
        loading={loading}
        /* The counters above are a filter the table cannot see: page 6 of six hundred
           keys stayed page 6 after narrowing to the eleven that are unset. */
        resetKey={filter ?? ''}
        getRowId={(row) => row.key}
      />
        </>
      )}

      {view === 'reference' && (
        <>
          {/* One line, but it explains a whole class of "the video never processed"
              reports, so it leads rather than hiding in a table. */}
          <p className="set-ffmpeg">
            <StatusBadge tone={ffmpegExists ? 'success' : 'danger'}>
              {ffmpegExists ? S.reference.present : S.reference.missing}
            </StatusBadge>
            <bdi>{S.reference.ffmpeg}</bdi>
          </p>

          <DataTable
            /* A second list on the same screen: the address already belongs to the first. */
            pageParam={null}
            columns={referenceColumns}
            rows={reference}
            loading={loading}
            initialSort={{ key: 'existsInDb', dir: 'asc' }}
            getRowId={(row) => `${row.group}-${row.id}`}
          />
        </>
      )}
    </>
  )
}
